import React from "react";
import { Box, Typography, Tooltip } from "@material-ui/core";
import { makeStyles } from "@material-ui/styles";
import {
  getFacturaEstadoColors,
  calcularDiasAtraso,
  getAtrasoStyle,
  badgeStyles,
} from "../styles/clientePortalStyles";
import { FiberManualRecord, Warning } from "@material-ui/icons";

// ==================== ESTILOS ====================

const useStyles = makeStyles((theme) => ({
  badge: {
    ...badgeStyles,
  },
  dot: {
    fontSize: 12,
  },
  warningIcon: {
    fontSize: 14,
  },
}));

/**
 * Componente PaymentBadge - Badge para estados de pago con indicador de atraso
 *
 * @param {string} estado - Estado de la factura
 * @param {string} fechaFacturacion - Fecha de facturación (para calcular atraso)
 * @param {boolean} showDot - Mostrar punto de color (default: true)
 * @param {boolean} showAtraso - Mostrar indicador de atraso si aplica (default: true)
 *
 * @example Badge simple
 * <PaymentBadge estado="PAGADO" />
 *
 * @example Badge con indicador de atraso
 * <PaymentBadge
 *   estado="PENDIENTE"
 *   fechaFacturacion="2024-10-15"
 * />
 *
 * @example Badge sin punto
 * <PaymentBadge estado="FACTURADO" showDot={false} />
 */
export default function PaymentBadge({
  estado,
  fechaFacturacion,
  showDot = true,
  showAtraso = true,
}) {
  const classes = useStyles();

  // Obtener colores del estado
  const colors = getFacturaEstadoColors(estado);

  // Calcular días de atraso si aplica
  const diasAtraso =
    showAtraso && fechaFacturacion
      ? calcularDiasAtraso(fechaFacturacion, estado)
      : 0;

  const atrasoStyle = diasAtraso > 0 ? getAtrasoStyle(diasAtraso) : null;

  // Si hay atraso, mostrar badge de atraso
  if (atrasoStyle) {
    return (
      <Tooltip title={`${diasAtraso} días de atraso`} arrow>
        <Box
          className={classes.badge}
          sx={{
            backgroundColor: atrasoStyle.backgroundColor,
            color: atrasoStyle.color,
          }}
        >
          <Warning className={classes.warningIcon} />
          <Typography
            variant="body2"
            component="span"
            sx={{
              fontSize: "inherit",
              fontWeight: "inherit",
            }}
          >
            {diasAtraso} días atraso
          </Typography>
        </Box>
      </Tooltip>
    );
  }

  // Badge normal de estado
  return (
    <Box
      className={classes.badge}
      sx={{
        backgroundColor: colors.bg,
        color: colors.text,
      }}
    >
      {showDot && (
        <FiberManualRecord className={classes.dot} sx={{ color: colors.dot }} />
      )}
      <Typography
        variant="body2"
        component="span"
        sx={{
          fontSize: "inherit",
          fontWeight: "inherit",
        }}
      >
        {colors.label}
      </Typography>
    </Box>
  );
}
